/**
 * RFC 9457 `application/problem+json` factories, matching the ratified
 * CODEX-3 contracts exactly:
 *
 * ```json
 * {
 *   "type": "urn:erp:problem:<code>",
 *   "title": "…", "status": 422, "detail": "…",
 *   "code": "<stable_machine_code>",
 *   "errors": [ { "field": "lines[2].qty", "code": "…", "message": "…" } ]
 * }
 * ```
 *
 * Domain mocks use a type URN whose suffix matches `code`. Platform middleware
 * problems use its canonical `about:blank` type and dotted code. `errors[]`
 * maps onto React Hook Form fields via CLAUDE-A's problem+json
 * form mapper; problems without `errors[]` surface as toast/inline banner.
 * The ratified problem objects are closed (`additionalProperties: false`) —
 * factories must only attach extension members a contract declares.
 */
import type { components } from "@erp/api-client";

export const PROBLEM_TYPE_BASE = "urn:erp:problem:";
export const PROBLEM_CONTENT_TYPE = "application/problem+json";

export interface FieldError {
  field: string;
  code: string;
  message: string;
}

type Schemas = components["schemas"];

/**
 * The mock package also serves older non-Catalog lanes. Their existing
 * fixtures use this finite, named extension vocabulary; there is deliberately
 * no string index signature and factories cannot invent extension members.
 */
export interface KnownMockProblemExtensions {
  current_etag?: string;
  order_id?: string | null;
  order_no?: string;
  blockers?: readonly {
    code: string;
    count?: number;
    balance?: unknown;
  }[];
  expected_barcode?: string | null;
  got_barcode?: string;
  assignee_id?: string | null;
  current_status?: string;
  attempted?: string;
  legal_next?: readonly string[];
  exposure?: {
    credit_limit: unknown;
    open_ar: unknown;
    confirmed_uninvoiced: unknown;
    this_order: unknown;
    over_by: unknown;
  };
}

/** Generated closed Catalog RFC 9457 shape plus finite legacy mock fields. */
export type Problem = Schemas["CatalogProblem"] & KnownMockProblemExtensions;

type ProblemExtensions = Omit<
  Partial<Problem>,
  "type" | "title" | "status" | "detail" | "code" | "errors"
>;

/** Base factory — every specific factory below goes through this. */
export function makeProblem(
  code: string,
  status: number,
  title: string,
  detail?: string,
  extensions?: ProblemExtensions,
): Problem {
  return {
    type: `${PROBLEM_TYPE_BASE}${code}`,
    title,
    status,
    ...(detail !== undefined ? { detail } : {}),
    code,
    ...extensions,
  };
}

/** 422 `validation_failed` with field-level `errors[]` for the RHF mapper. */
export function validationProblem(fieldErrors: FieldError[], detail?: string): Problem {
  return {
    ...makeProblem(
      "validation_failed",
      422,
      "Validation failed",
      detail ?? "One or more fields are invalid.",
    ),
    errors: fieldErrors,
  };
}

/** 409 conflict with a caller-supplied stable machine code. */
export function conflictProblem(
  code: string,
  detail?: string,
  extensions?: ProblemExtensions,
): Problem {
  return makeProblem(code, 409, "Conflict", detail, extensions);
}

/** 404 `not_found` (no extension members — the ratified problems are closed). */
export function notFound(resource = "resource", detail?: string): Problem {
  return makeProblem("not_found", 404, "Not found", detail ?? `${resource} not found`);
}

/** 403 `permission_denied`, naming the missing permission in `detail` only. */
export function forbidden(permission: string): Problem {
  return makeProblem("permission_denied", 403, "Forbidden", `Missing permission: ${permission}`);
}

/**
 * 412 `resource_version_conflict` — ETag/If-Match mismatch. The ratified
 * shape requires the `current_etag` extension so a stale caller can refresh.
 */
export function preconditionFailed(currentEtag: string, detail?: string): Problem {
  return makeProblem(
    "resource_version_conflict",
    412,
    "Resource version conflict",
    detail ?? "The record was changed elsewhere; refresh and re-apply your edits.",
    { current_etag: currentEtag },
  );
}

/** Catalog's closed 412 shape intentionally declares no `current_etag`. */
export function catalogPreconditionFailed(detail?: string): Schemas["CatalogProblem"] {
  return {
    type: `${PROBLEM_TYPE_BASE}resource_version_conflict`,
    title: "Resource version conflict",
    status: 412,
    code: "resource_version_conflict",
    detail: detail ?? "The record was changed elsewhere; refresh and re-apply your edits.",
  };
}

/** Canonical platform 409 — same Idempotency-Key, different request bytes. */
export function idempotencyReuse(detail?: string): Problem {
  return {
    type: "about:blank",
    title: "Idempotency Key Reuse",
    status: 409,
    code: "platform.idempotency.key_reuse",
    detail: detail ?? "This Idempotency-Key was already used with a different request.",
  };
}

export type CatalogAttributeInUseProblem = Schemas["CatalogAttributeInUseProblem"];
export type CatalogAttributeOptionInUseProblem = Schemas["CatalogAttributeOptionInUseProblem"];
export type CatalogBarcodeDuplicateProblem = Schemas["CatalogBarcodeDuplicateProblem"];
export type CatalogBomCycleDetectedProblem = Schemas["CatalogBomCycleDetectedProblem"];
export type CatalogImportAlreadyCommittedProblem = Schemas["CatalogImportAlreadyCommittedProblem"];
export type CatalogImportUnknownHeaderProblem = Schemas["CatalogImportUnknownHeaderProblem"];
export type CatalogUomUnconvertibleProblem = Schemas["CatalogUomUnconvertibleProblem"];

export function catalogAttributeInUse(
  attributeId: string,
  usageCount: number,
  detail: string,
): CatalogAttributeInUseProblem {
  return {
    ...makeProblem("attribute_in_use", 422, "Attribute in use", detail),
    code: "attribute_in_use",
    attribute_id: attributeId,
    usage_count: usageCount,
  };
}

export function catalogAttributeOptionInUse(
  attributeId: string,
  option: string,
  usageCount: number,
  detail: string,
): CatalogAttributeOptionInUseProblem {
  return {
    ...makeProblem("attribute_option_in_use", 422, "Attribute option in use", detail),
    code: "attribute_option_in_use",
    attribute_id: attributeId,
    option,
    usage_count: usageCount,
  };
}

export function catalogBomCycleDetected(
  cycle: readonly string[],
  detail: string,
): CatalogBomCycleDetectedProblem {
  return {
    ...makeProblem("bom_cycle_detected", 422, "Component cycle detected", detail),
    code: "bom_cycle_detected",
    cycle,
  };
}

export function catalogBarcodeDuplicate(
  barcode: string,
  owner: Schemas["CatalogBarcodeOwner"],
): CatalogBarcodeDuplicateProblem {
  return {
    ...conflictProblem("barcode_duplicate", `${barcode} is already in use.`),
    code: "barcode_duplicate",
    owner,
  };
}

export function catalogUomUnconvertible(
  fromUom: string,
  toUom: string,
  detail: string,
): CatalogUomUnconvertibleProblem {
  return {
    ...makeProblem("uom_unconvertible", 422, "Units are unconvertible", detail),
    code: "uom_unconvertible",
    from_uom: fromUom,
    to_uom: toUom,
  };
}

export function catalogImportAlreadyCommitted(
  importId: string,
  detail: string,
): CatalogImportAlreadyCommittedProblem {
  return {
    ...conflictProblem("import_already_committed", detail),
    code: "import_already_committed",
    import_id: importId,
  };
}

export function catalogImportUnknownHeader(detail: string): CatalogImportUnknownHeaderProblem {
  return {
    ...makeProblem("import_unknown_header", 422, "Unknown CSV header", detail),
    code: "import_unknown_header",
  };
}
